import {
  Box,
  Flex,
  Input,
  Radio,
  Text,
  Button as ChakraButton,
  VStack,
  HStack,
  IconButton,
  useColorModeValue,
  Divider,
  Tag,
} from "@chakra-ui/react";
import React, { useState, useEffect, useMemo } from "react";
import colors from "../../utils/colors";
import { useFormik } from "formik";
import * as yup from "yup";
import { v4 as uuidv4 } from "uuid";
import { useUpdateDoctor } from "../../apis/doctor";
import { useAppSelector } from "../../hooks/useAppSelector";
import { useAppDispatch } from "../../hooks/useAppDispatch";
import { setDoctorInfo } from "../../store/authSlice";
import { TDoctorInfo, TClinicAddress } from "../../apis/types";
import { EditIcon } from "@chakra-ui/icons";
import customToast, { ToastType } from "../../components/Toast";

interface FormAddress extends TClinicAddress {
  formId: string;
}

const ClinicAddressCard = () => {
  const { doctorInfo } = useAppSelector((state) => state.auth);
  const dispatch = useAppDispatch();
  const { mutate: updateDoctor, isPending } = useUpdateDoctor();
  const [isEditing, setIsEditing] = useState(false);

  const textColor = useColorModeValue(colors.dark, "gray.100");
  const subtleTextColor = useColorModeValue("gray.600", "gray.300");
  const labelColor = useColorModeValue(colors.dark, "gray.200");
  const displayItemBorderColor = useColorModeValue("gray.200", "gray.600");
  const editItemBorderColor = useColorModeValue("gray.300", "gray.600");

  const mapClinicsToFormAddresses = (
    clinics?: TClinicAddress[]
  ): FormAddress[] => {
    if (clinics && clinics.length > 0) {
      return clinics.map((item) => ({
        ...item,
        formId: item.id || uuidv4(),
      }));
    }
    return [
      {
        formId: uuidv4(),
        name: "",
        address: "",
        city: "",
        state: "",
        country: "",
        isDefault: true,
      },
    ];
  };

  const getInitialFormAddresses = useMemo(() => {
    return mapClinicsToFormAddresses(doctorInfo?.clinics);
  }, [doctorInfo?.clinics]);

  const formik = useFormik<{ addresses: FormAddress[] }>({
    initialValues: {
      addresses: getInitialFormAddresses,
    },
    validationSchema: yup.object({
      addresses: yup
        .array()
        .of(
          yup.object({
            id: yup.string().optional(),
            name: yup.string().required("Clinic name is required"),
            address: yup.string().required("Address is required"),
            state: yup.string().required("State is required"),
            city: yup.string().required("City is required"),
            isDefault: yup.boolean(),
            postalCode: yup.string().nullable(),
            country: yup.string().required(),
            formId: yup.string().required(),
          })
        )
        .min(1, "At least one clinic address is required.")
        .required("Addresses are required"),
    }),
    onSubmit: (values) => {
      //console.log("Form submitted:", values);
      const clinicsToSave: TClinicAddress[] = values.addresses.map((addr) => {
        const { formId, ...apiAddress } = addr;
        return apiAddress as TClinicAddress;
      });
      // console.log("Calling updateDoctor with:", clinicsToSave);

      updateDoctor(
        {
          data: { clinics: clinicsToSave },
          doctorId: doctorInfo?._id,
        },

        {
          onSuccess: (response: any) => {
            customToast("Clinic addresses updated!", ToastType.success);
            const returnedAddressesFromApi = response?.data?.clinics;

            if (
              returnedAddressesFromApi &&
              Array.isArray(returnedAddressesFromApi)
            ) {
              const updatedDoctorData = {
                ...doctorInfo,
                clinics: returnedAddressesFromApi.map((addr) => ({
                  ...addr,
                })),
              } as TDoctorInfo;
              dispatch(setDoctorInfo(updatedDoctorData));

              const sessionDoc = sessionStorage.getItem("created-doctor");
              if (sessionDoc) {
                try {
                  const parsedSessionDoc = JSON.parse(
                    sessionDoc
                  ) as TDoctorInfo;
                  const updatedSessionDoc = {
                    ...parsedSessionDoc,
                    clinics: returnedAddressesFromApi.map((addr) => ({
                      ...addr,
                    })),
                  };
                  sessionStorage.setItem(
                    "created-doctor",
                    JSON.stringify(updatedSessionDoc)
                  );
                } catch (e) {
                  console.error(
                    "Error updating session storage for clinic addresses",
                    e
                  );
                }
              }
            } else {
              console.warn(
                "API response for clinic addresses was not as expected. Client data might be used as fallback."
              );
              const fallbackAddresses = clinicsToSave.map((c) => ({
                ...c,
                id: c.id || uuidv4(),
              }));
              const updatedDoctorData = {
                ...doctorInfo,
                clinics: fallbackAddresses,
              } as TDoctorInfo;
              dispatch(setDoctorInfo(updatedDoctorData));
            }
            setIsEditing(false);
          },
          onError: (error: any) => {
            customToast(
              error?.response?.data?.message || "Update failed.",
              ToastType.error
            );
          },
        }
      );
    },
  });

  useEffect(() => {
    if (isEditing) {
      formik.resetForm({
        values: {
          addresses: mapClinicsToFormAddresses(doctorInfo?.clinics),
        },
      });
    }
  }, [isEditing, doctorInfo?.clinics]);

  const handleEditClick = () => {
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    formik.resetForm({
      values: {
        addresses: mapClinicsToFormAddresses(doctorInfo?.clinics),
      },
    });
  };

  const handleSetDefault = (formId: string) => {
    const updatedAddresses = formik.values.addresses.map((addr) => ({
      ...addr,
      isDefault: addr.formId === formId,
    }));
    formik.setFieldValue("addresses", updatedAddresses);
  };

  const handleRemoveAddress = (formId: string) => {
    let filteredAddresses = formik.values.addresses.filter(
      (addr) => addr.formId !== formId
    );
    if (filteredAddresses.length === 0) {
      filteredAddresses = [
        {
          formId: uuidv4(),
          name: "",
          address: "",
          city: "",
          state: "",
          country: "",

          isDefault: true,
        },
      ];
    } else {
      const wasDefaultRemoved = !filteredAddresses.some(
        (addr) => addr.isDefault
      );
      if (wasDefaultRemoved) {
        filteredAddresses[0].isDefault = true;
      }
    }
    formik.setFieldValue("addresses", filteredAddresses);
  };

  const handleAddNewAddress = () => {
    const newAddress: FormAddress = {
      formId: uuidv4(),
      name: "",
      address: "",
      city: "",
      state: "",
      country: "",
      isDefault: formik.values.addresses.length === 0,
    };
    formik.setFieldValue("addresses", [...formik.values.addresses, newAddress]);
  };

  if (!isEditing) {
    return (
      <Box>
        <Flex justifyContent="space-between" alignItems="center" mb={4}>
          <Text
            fontFamily="lato"
            fontWeight="bold"
            fontSize="xl"
            color={textColor}
          >
            Clinic Addresses
          </Text>
          <IconButton
            aria-label="Edit clinic addresses"
            icon={<EditIcon />}
            size="sm"
            variant="ghost"
            onClick={handleEditClick}
          />
        </Flex>

        {doctorInfo?.clinics &&
        doctorInfo.clinics.length > 0 &&
        doctorInfo.clinics.some((addr) => addr.name || addr.address) ? (
          <VStack spacing={4} align="stretch">
            {doctorInfo.clinics
              .filter((addr) => addr.name || addr.address)
              .map((addr, index) => (
                <Box
                  key={addr.id || index}
                  p={3}
                  borderWidth="1px"
                  borderRadius="md"
                  borderColor={displayItemBorderColor}
                >
                  <Text fontWeight="semibold" color={textColor}>
                    {addr.name || `Address ${index + 1}`}
                  </Text>
                  <Text fontSize="sm" color={subtleTextColor}>
                    {addr.address}
                  </Text>
                  <Text fontSize="sm" color={subtleTextColor}>
                    {addr.city}, {addr.state}
                  </Text>
                  {addr.postalCode && (
                    <Text fontSize="sm" color={subtleTextColor}>
                      {addr.postalCode}
                    </Text>
                  )}
                  {addr.country && (
                    <Text fontSize="sm" color={subtleTextColor}>
                      {addr.country}
                    </Text>
                  )}
                  {addr.isDefault && (
                    <Tag size="sm" colorScheme="green" mt={1}>
                      Default
                    </Tag>
                  )}
                </Box>
              ))}
          </VStack>
        ) : (
          <Text color={subtleTextColor}>No clinic addresses added yet.</Text>
        )}
      </Box>
    );
  }

  return (
    <Box>
      <Text
        fontFamily="lato"
        fontWeight="bold"
        fontSize="xl"
        color={textColor}
        mb={4}
      >
        Edit Clinic Addresses
      </Text>
      <form onSubmit={formik.handleSubmit}>
        <VStack spacing={6} align="stretch">
          {formik.values.addresses.map((val, index) => (
            <Box
              key={val.formId}
              p={4}
              borderWidth="1px"
              borderRadius="md"
              borderColor={editItemBorderColor}
            >
              <VStack spacing={3} align="stretch">
                <Box>
                  <Text
                    fontFamily="lato"
                    fontWeight={400}
                    fontSize="sm"
                    color={labelColor}
                    mb="8px"
                  >
                    Clinic Name
                  </Text>
                  <Input
                    name={`addresses[${index}].name`}
                    value={val.name || ""}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    isInvalid={
                      !!(
                        formik.touched.addresses?.[index]?.name &&
                        formik.errors.addresses?.[index]?.["name"]
                      )
                    }
                    placeholder="e.g., Main Street Clinic"
                    size="sm"
                  />
                  {formik.touched.addresses?.[index]?.name &&
                    formik.errors.addresses?.[index]?.["name"] && (
                      <Text color="red.500" fontSize="xs" mt={1}>
                        {formik.errors.addresses?.[index]?.["name"]}
                      </Text>
                    )}
                </Box>
                <Box>
                  <Text
                    fontFamily="lato"
                    fontWeight={400}
                    fontSize="sm"
                    color={labelColor}
                    mb="8px"
                  >
                    Address Line 1
                  </Text>
                  <Input
                    name={`addresses[${index}].address`}
                    value={val.address || ""}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    isInvalid={
                      !!(
                        formik.touched.addresses?.[index]?.address &&
                        formik.errors.addresses?.[index]?.["address"]
                      )
                    }
                    placeholder="Street address"
                    size="sm"
                  />
                  {formik.touched.addresses?.[index]?.address &&
                    formik.errors.addresses?.[index]?.["address"] && (
                      <Text color="red.500" fontSize="xs" mt={1}>
                        {formik.errors.addresses?.[index]?.["address"]}
                      </Text>
                    )}
                </Box>
                <HStack spacing={3}>
                  <Box flex={1}>
                    <Text
                      fontFamily="lato"
                      fontWeight={400}
                      fontSize="sm"
                      color={labelColor}
                      mb="8px"
                    >
                      City
                    </Text>
                    <Input
                      name={`addresses[${index}].city`}
                      value={val.city || ""}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      isInvalid={
                        !!(
                          formik.touched.addresses?.[index]?.city &&
                          formik.errors.addresses?.[index]?.["city"]
                        )
                      }
                      size="sm"
                    />
                    {formik.touched.addresses?.[index]?.city &&
                      formik.errors.addresses?.[index]?.["city"] && (
                        <Text color="red.500" fontSize="xs" mt={1}>
                          {formik.errors.addresses?.[index]?.["city"]}
                        </Text>
                      )}
                  </Box>
                  <Box flex={1}>
                    <Text
                      fontFamily="lato"
                      fontWeight={400}
                      fontSize="sm"
                      color={labelColor}
                      mb="8px"
                    >
                      State
                    </Text>
                    <Input
                      name={`addresses[${index}].state`}
                      value={val.state || ""}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      isInvalid={
                        !!(
                          formik.touched.addresses?.[index]?.state &&
                          formik.errors.addresses?.[index]?.["state"]
                        )
                      }
                      size="sm"
                    />
                    {formik.touched.addresses?.[index]?.state &&
                      formik.errors.addresses?.[index]?.["state"] && (
                        <Text color="red.500" fontSize="xs" mt={1}>
                          {formik.errors.addresses?.[index]?.["state"]}
                        </Text>
                      )}
                  </Box>
                </HStack>
                <HStack spacing={3}>
                  <Box flex={1}>
                    <Text
                      fontFamily="lato"
                      fontWeight={400}
                      fontSize="sm"
                      color={labelColor}
                      mb="8px"
                    >
                      Postal Code (Optional)
                    </Text>
                    <Input
                      name={`addresses[${index}].postalCode`}
                      value={val.postalCode || ""}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      size="sm"
                    />
                  </Box>
                  <Box flex={1}>
                    <Text
                      fontFamily="lato"
                      fontWeight={400}
                      fontSize="sm"
                      color={labelColor}
                      mb="8px"
                    >
                      Country
                    </Text>
                    <Input
                      name={`addresses[${index}].country`}
                      value={val.country || ""}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      size="sm"
                    />
                  </Box>
                </HStack>
                <Flex alignItems="center" justifyContent="space-between" mt={2}>
                  <HStack spacing={2}>
                    <Radio
                      isChecked={val.isDefault}
                      onChange={() => handleSetDefault(val.formId)}
                      colorScheme="green"
                    />
                    <Text fontSize="sm" color={labelColor}>
                      Set as default
                    </Text>
                  </HStack>
                  {formik.values.addresses.length > 1 && (
                    <ChakraButton
                      size="xs"
                      colorScheme="red"
                      variant="ghost"
                      onClick={() => handleRemoveAddress(val.formId)}
                    >
                      Remove
                    </ChakraButton>
                  )}
                </Flex>
              </VStack>
              {index < formik.values.addresses.length - 1 && <Divider my={4} />}
            </Box>
          ))}
        </VStack>

        <ChakraButton
          mt={4}
          size="sm"
          colorScheme="teal"
          variant="outline"
          onClick={handleAddNewAddress}
          isDisabled={isPending}
        >
          + Add New Address
        </ChakraButton>

        <Flex gap={3} justifyContent="flex-end" mt={6}>
          <ChakraButton
            variant="ghost"
            onClick={handleCancelEdit}
            size="sm"
            isDisabled={isPending}
          >
            Cancel
          </ChakraButton>
          <ChakraButton
            bg={colors.primary}
            color={colors.white}
            _hover={{ bg: colors.secondaryBlack || colors.primary }}
            type="submit"
            size="sm"
            isLoading={isPending}
            isDisabled={isPending || !formik.dirty || !formik.isValid}
          >
            Save Changes
          </ChakraButton>
        </Flex>
      </form>
    </Box>
  );
};

export default ClinicAddressCard;
